export type Lang = 'en' | 'vi' | 'zh';

export interface LanguageInfo {
  code: Lang;
  name: string;
  nativeName: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
];

export const LANG_DATA: Record<Lang, Record<string, string>> = {
  en: {
    // Main Menu
    game_title: 'EARTH DEFENSE',
    game_subtitle: 'Tower Defense Game',
    start: 'START GAME',
    profile: 'PROFILE',
    achievements: 'ACHIEVEMENTS',
    leaderboard: 'LEADERBOARD',
    settings: 'SETTINGS',
    back: 'BACK',
    version: 'Version 1.0.0',
    
    // Game UI
    level: 'Level',
    xp: 'XP',
    kills: 'Kills',
    credits: 'Credits',
    gold: 'Gold',
    ruby: 'Ruby',
    hp: 'HP',
    dr: 'Defense',
    towers: 'Towers',
    ability: 'Ability',
    damage: 'Damage',
    range: 'Range',
    attack_speed: 'Attack Speed',
    
    // Earth Selection
    select_earth: 'SELECT EARTH',
    earth_stats: 'Earth Stats',
    choose: 'CHOOSE',
    unlock_at_level: 'Unlock at Level',
    max_towers: 'Max Towers',
    
    // Earth Types
    earth_classic: 'Classic Earth',
    earth_ice: 'Ice Earth',
    earth_volcanic: 'Volcanic Earth',
    earth_crystal: 'Crystal Earth',
    earth_quantum: 'Quantum Earth',
    earth_neutron: 'Neutron Earth',
    
    // Earth Descriptions
    earth_classic_desc: 'Balanced stats, perfect for beginners',
    earth_ice_desc: 'High health, freezes nearby monsters',
    earth_volcanic_desc: 'Reflects damage, supports many towers',
    earth_crystal_desc: 'High resistance, generates energy over time',
    earth_quantum_desc: 'Can teleport towers once per level',
    earth_neutron_desc: 'Massive health, slows all monsters',
    
    // Tower Names
    laser_tower: 'Laser Tower',
    rapid_fire_tower: 'Rapid Fire',
    heavy_cannon: 'Heavy Cannon',
    missile_tower: 'Missile Tower',
    multi_directional_tower: 'Multi-Directional',
    piercing_tower: 'Piercing Tower',
    circular_tower: 'Circular Tower',
    chain_lightning_tower: 'Chain Lightning',
    freeze_tower: 'Freeze Tower',
    shotgun_tower: 'Shotgun Tower',
    beam_tower: 'Beam Tower',
    
    // Tower Descriptions
    laser_tower_desc: 'Balanced damage and speed',
    rapid_fire_tower_desc: 'High attack speed, low damage',
    heavy_cannon_desc: 'High damage, slow attack',
    missile_tower_desc: 'Area damage explosive',
    multi_directional_tower_desc: 'Shoots in 4 directions',
    piercing_tower_desc: 'Bullets pierce through enemies',
    circular_tower_desc: 'Shoots in all directions',
    chain_lightning_tower_desc: 'Lightning chains to 3 targets',
    freeze_tower_desc: 'Slows and freezes enemies',
    shotgun_tower_desc: 'Fires 5 bullets in cone',
    beam_tower_desc: 'Continuous beam damage',
    
    // Pet Names
    laser_pet: 'Laser Pet',
    missile_pet: 'Missile Pet',
    drone_pet: 'Drone Pet',
    shield_pet: 'Shield Pet',
    support_pet: 'Support Pet',
    
    // Pet Descriptions
    laser_pet_desc: 'Fast laser attacks',
    missile_pet_desc: 'Explosive missile attacks',
    drone_pet_desc: 'Autonomous combat drone',
    shield_pet_desc: 'Provides shield protection',
    support_pet_desc: 'Buffs nearby towers',
    
    // Profile UI
    owned: 'Owned',
    equipped: 'Equipped',
    buy: 'BUY',
    equip: 'EQUIP',
    unequip: 'UNEQUIP',
    upgrade: 'UPGRADE',
    combine: 'COMBINE',
    cost: 'Cost',
    level_req: 'Level Required',
    already_owned: 'Already Owned',
    not_enough_currency: 'Not enough currency',
    
    // Equipment
    equipment: 'Equipment',
    weapon: 'Weapon',
    armor: 'Armor',
    accessory: 'Accessory',
    special: 'Special',
    common: 'Common',
    rare: 'Rare',
    epic: 'Epic',
    legendary: 'Legendary',
    
    // Achievements
    achievement_unlocked: 'ACHIEVEMENT UNLOCKED!',
    achievements_completed: 'Completed',
    achievements_progress: 'Progress',
    claim_reward: 'CLAIM!',
    achievement_reward: 'Achievement Reward Claimed!',
    
    // Achievement Names
    first_steps: 'First Steps',
    survivor_10: 'Survivor',
    veteran_25: 'Veteran',
    master_50: 'Master Defender',
    legendary_100: 'Legendary Guardian',
    exterminator_100: 'Exterminator',
    slayer_1000: 'Monster Slayer',
    boss_hunter_10: 'Boss Hunter',
    architect_50: 'Tower Architect',
    wealthy_10k: 'Wealthy',
    gem_collector_100: 'Gem Collector',
    earth_collector: 'Planet Collector',
    tower_master: 'Tower Master',
    perfect_game: 'Perfect Defense',
    speed_runner: 'Speed Runner',
    daily_devotee: 'Daily Devotee',
    
    // Achievement Descriptions
    first_steps_desc: 'Complete your first game',
    survivor_10_desc: 'Reach level 10',
    veteran_25_desc: 'Reach level 25',
    master_50_desc: 'Reach level 50',
    legendary_100_desc: 'Reach level 100',
    exterminator_100_desc: 'Kill 100 monsters',
    slayer_1000_desc: 'Kill 1,000 monsters',
    boss_hunter_10_desc: 'Defeat 10 bosses',
    architect_50_desc: 'Build 50 towers',
    wealthy_10k_desc: 'Earn 10,000 total gold',
    gem_collector_100_desc: 'Earn 100 total ruby',
    earth_collector_desc: 'Unlock all Earth types',
    tower_master_desc: 'Unlock all tower types',
    perfect_game_desc: 'Complete a game without Earth taking damage',
    speed_runner_desc: 'Reach level 20 in under 10 minutes',
    daily_devotee_desc: 'Login for 7 consecutive days',
    
    // Leaderboard
    highest_level: 'Highest Level',
    monster_slayer: 'Monster Slayer',
    survival_time: 'Survival Master',
    speed_run: 'Speed Runners',
    composite_score: 'Master Defenders',
    rank: 'RANK',
    player: 'PLAYER',
    score: 'SCORE',
    date: 'DATE',
    your_best: 'YOUR BEST',
    no_entries: 'No entries yet. Be the first!',
    new_record: 'NEW RECORD!',
    leaderboard_stats: 'LEADERBOARD STATS',
    total_entries: 'Total Entries',
    top_score: 'Top Score',
    average: 'Average',
    
    // Seasonal
    season: 'Season',
    time_left: 'left',
    season_champion: 'Season Champion',
    season_runner_up: 'Season Runner-up',
    season_bronze: 'Season Bronze',
    
    // Audio Settings
    audio_settings: 'AUDIO SETTINGS',
    master_volume: 'Master Volume',
    music_volume: 'Music Volume',
    sfx_volume: 'SFX Volume',
    mute: 'MUTE',
    unmute: 'UNMUTE',
    
    // Visual Settings
    visual_settings: 'VISUAL SETTINGS',
    show_fps: 'Show FPS',
    particle_effects: 'Particle Effects',
    screen_shake: 'Screen Shake',
    quality_presets: 'QUALITY PRESETS',
    low: 'LOW',
    medium: 'MEDIUM',
    high: 'HIGH',
    
    // Language Settings
    language_settings: 'LANGUAGE SETTINGS',
    select_language: 'Select Language',
    
    // Ad System
    watch_ad: 'WATCH AD',
    free_rewards: 'Free Rewards',
    daily_rewards: 'Daily Rewards',
    streak: 'Streak',
    claim: 'CLAIM!',
    cooldown: 'Cooldown',
    revive_earth: 'Revive Earth',
    double_credits: 'Double Credits',
    tower_boost: 'Tower Boost',
    pet_boost: 'Pet Boost',
    
    // Game Over
    game_over: 'GAME OVER',
    final_level: 'Final Level',
    monsters_killed: 'Monsters Killed',
    bosses_killed: 'Bosses Killed',
    rewards: 'Rewards',
    total_gold: 'Total Gold',
    total_ruby: 'Total Ruby',
    bonus: 'Bonus',
    restart: 'RESTART',
    main_menu: 'MAIN MENU',
    
    // Notifications
    earth_unlocked: 'New Earth Unlocked!',
    tower_unlocked: 'New Tower Unlocked!',
    pet_unlocked: 'New Pet Unlocked!',
    level_up: 'LEVEL UP!',
    boss_warning: 'BOSS INCOMING!',
    purchase_successful: 'Purchase Successful!',
    upgrade_successful: 'Upgrade Successful!',
    
    // Common UI
    ok: 'OK',
    cancel: 'CANCEL',
    yes: 'YES',
    no: 'NO',
    loading: 'Loading...',
    awesome: 'AWESOME!',
    congratulations: 'Congratulations!',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    close: 'CLOSE',
    continue: 'CONTINUE',
  },
  
  vi: {
    // Main Menu
    game_title: 'THỦ THÀNH TRÁI ĐẤT',
    game_subtitle: 'Game Thủ Thành',
    start: 'BẮT ĐẦU GAME',
    profile: 'HỒ SƠ',
    achievements: 'THÀNH TÍCH',
    leaderboard: 'BẢNG XẾP HẠNG',
    settings: 'CÀI ĐẶT',
    back: 'QUAY LẠI',
    version: 'Phiên bản 1.0.0',
    
    // Game UI
    level: 'Cấp',
    xp: 'XP',
    kills: 'Tiêu diệt',
    credits: 'Tín dụng',
    gold: 'Vàng',
    ruby: 'Ruby',
    hp: 'Máu',
    dr: 'Phòng thủ',
    towers: 'Tháp',
    ability: 'Kỹ năng',
    damage: 'Sát thương',
    range: 'Tầm bắn',
    attack_speed: 'Tốc độ tấn công',
    
    // Earth Selection
    select_earth: 'CHỌN TRÁI ĐẤT',
    earth_stats: 'Thông số Trái đất',
    choose: 'CHỌN',
    unlock_at_level: 'Mở khóa ở cấp',
    max_towers: 'Tối đa tháp',
    
    // Earth Types
    earth_classic: 'Trái Đất Cổ Điển',
    earth_ice: 'Trái Đất Băng',
    earth_volcanic: 'Trái Đất Núi Lửa',
    earth_crystal: 'Trái Đất Tinh Thể',
    earth_quantum: 'Trái Đất Lượng Tử',
    earth_neutron: 'Trái Đất Neutron',
    
    // Earth Descriptions
    earth_classic_desc: 'Cân bằng, hoàn hảo cho người mới',
    earth_ice_desc: 'Máu cao, đóng băng quái vật gần đó',
    earth_volcanic_desc: 'Phản sát thương, hỗ trợ nhiều tháp',
    earth_crystal_desc: 'Kháng cao, tự sinh năng lượng theo thời gian',
    earth_quantum_desc: 'Có thể dịch chuyển tháp 1 lần mỗi màn',
    earth_neutron_desc: 'Máu khổng lồ, làm chậm mọi quái vật',
    
    // Tower Names
    laser_tower: 'Tháp Laser',
    rapid_fire_tower: 'Bắn Nhanh',
    heavy_cannon: 'Đại Bác',
    missile_tower: 'Tháp Tên Lửa',
    multi_directional_tower: 'Đa Hướng',
    piercing_tower: 'Xuyên Thấu',
    circular_tower: 'Vòng Tròn',
    chain_lightning_tower: 'Sét Lan',
    freeze_tower: 'Tháp Băng',
    shotgun_tower: 'Shotgun',
    beam_tower: 'Tia Liên Tục',
    
    // Tower Descriptions
    laser_tower_desc: 'Sát thương và tốc độ cân bằng',
    rapid_fire_tower_desc: 'Tốc độ cao, sát thương thấp',
    heavy_cannon_desc: 'Sát thương cao, tấn công chậm',
    missile_tower_desc: 'Sát thương diện rộng nổ',
    multi_directional_tower_desc: 'Bắn 4 hướng',
    piercing_tower_desc: 'Đạn xuyên qua kẻ thù',
    circular_tower_desc: 'Bắn mọi hướng',
    chain_lightning_tower_desc: 'Sét lan 3 mục tiêu',
    freeze_tower_desc: 'Làm chậm và đóng băng',
    shotgun_tower_desc: 'Bắn 5 viên hình nón',
    beam_tower_desc: 'Sát thương tia liên tục',
    
    // Pet Names
    laser_pet: 'Pet Laser',
    missile_pet: 'Pet Tên Lửa',
    drone_pet: 'Pet Drone',
    shield_pet: 'Pet Khiên',
    support_pet: 'Pet Hỗ Trợ',
    
    // Pet Descriptions
    laser_pet_desc: 'Tấn công laser nhanh',
    missile_pet_desc: 'Tấn công tên lửa nổ',
    drone_pet_desc: 'Drone chiến đấu tự động',
    shield_pet_desc: 'Cung cấp bảo vệ khiên',
    support_pet_desc: 'Tăng cường tháp gần đó',
    
    // Profile UI
    owned: 'Sở hữu',
    equipped: 'Đang trang bị',
    buy: 'MUA',
    equip: 'TRANG BỊ',
    unequip: 'BỎ TRANG BỊ',
    upgrade: 'NÂNG CẤP',
    combine: 'KẾT HỢP',
    cost: 'Giá',
    level_req: 'Yêu cầu cấp',
    already_owned: 'Đã sở hữu',
    not_enough_currency: 'Không đủ tiền',
    
    // Equipment
    equipment: 'Trang bị',
    weapon: 'Vũ khí',
    armor: 'Giáp',
    accessory: 'Phụ kiện',
    special: 'Đặc biệt',
    common: 'Thường',
    rare: 'Hiếm',
    epic: 'Sử thi',
    legendary: 'Huyền thoại',
    
    // Achievements
    achievement_unlocked: 'MỞ KHÓA THÀNH TÍCH!',
    achievements_completed: 'Hoàn thành',
    achievements_progress: 'Tiến trình',
    claim_reward: 'NHẬN!',
    achievement_reward: 'Đã nhận thưởng thành tích!',
    
    // Achievement Names
    first_steps: 'Bước Đầu',
    survivor_10: 'Người Sống Sót',
    veteran_25: 'Kỳ Cựu',
    master_50: 'Bậc Thầy Phòng Thủ',
    legendary_100: 'Hộ Vệ Huyền Thoại',
    exterminator_100: 'Diệt Trừ',
    slayer_1000: 'Sát Thủ Quái Vật',
    boss_hunter_10: 'Thợ Săn Boss',
    architect_50: 'Kiến Trúc Sư Tháp',
    wealthy_10k: 'Giàu Có',
    gem_collector_100: 'Thu Thập Ngọc',
    earth_collector: 'Thu Thập Hành Tinh',
    tower_master: 'Bậc Thầy Tháp',
    perfect_game: 'Phòng Thủ Hoàn Hảo',
    speed_runner: 'Người Chạy Tốc Độ',
    daily_devotee: 'Người Tận Tâm Hàng Ngày',
    
    // Achievement Descriptions
    first_steps_desc: 'Hoàn thành game đầu tiên',
    survivor_10_desc: 'Đạt cấp 10',
    veteran_25_desc: 'Đạt cấp 25',
    master_50_desc: 'Đạt cấp 50',
    legendary_100_desc: 'Đạt cấp 100',
    exterminator_100_desc: 'Tiêu diệt 100 quái vật',
    slayer_1000_desc: 'Tiêu diệt 1.000 quái vật',
    boss_hunter_10_desc: 'Đánh bại 10 boss',
    architect_50_desc: 'Xây 50 tháp',
    wealthy_10k_desc: 'Kiếm 10.000 vàng tổng',
    gem_collector_100_desc: 'Kiếm 100 ruby tổng',
    earth_collector_desc: 'Mở khóa tất cả loại Trái Đất',
    tower_master_desc: 'Mở khóa tất cả loại tháp',
    perfect_game_desc: 'Hoàn thành game mà Trái Đất không bị sát thương',
    speed_runner_desc: 'Đạt cấp 20 dưới 10 phút',
    daily_devotee_desc: 'Đăng nhập 7 ngày liên tiếp',
    
    // Leaderboard
    highest_level: 'Cấp Cao Nhất',
    monster_slayer: 'Sát Thủ Quái Vật',
    survival_time: 'Bậc Thầy Sinh Tồn',
    speed_run: 'Người Chạy Tốc Độ',
    composite_score: 'Bậc Thầy Phòng Thủ',
    rank: 'HẠNG',
    player: 'NGƯỜI CHƠI',
    score: 'ĐIỂM',
    date: 'NGÀY',
    your_best: 'TỐT NHẤT CỦA BẠN',
    no_entries: 'Chưa có ai. Hãy là người đầu tiên!',
    new_record: 'KỶ LỤC MỚI!',
    leaderboard_stats: 'THỐNG KÊ BẢNG XẾP HẠNG',
    total_entries: 'Tổng Số Mục',
    top_score: 'Điểm Cao Nhất',
    average: 'Trung Bình',
    
    // Seasonal
    season: 'Mùa',
    time_left: 'còn lại',
    season_champion: 'Vô Địch Mùa',
    season_runner_up: 'Á Quân Mùa',
    season_bronze: 'Hạng Ba Mùa',
    
    // Audio Settings
    audio_settings: 'CÀI ĐẶT ÂM THANH',
    master_volume: 'Âm Lượng Chính',
    music_volume: 'Âm Lượng Nhạc',
    sfx_volume: 'Âm Lượng Hiệu Ứng',
    mute: 'TẮT TIẾNG',
    unmute: 'BẬT TIẾNG',
    
    // Visual Settings
    visual_settings: 'CÀI ĐẶT HÌNH ẢNH',
    show_fps: 'Hiển Thị FPS',
    particle_effects: 'Hiệu Ứng Hạt',
    screen_shake: 'Rung Màn Hình',
    quality_presets: 'CÀI ĐẶT CHẤT LƯỢNG',
    low: 'THẤP',
    medium: 'TRUNG BÌNH',
    high: 'CAO',
    
    // Language Settings
    language_settings: 'CÀI ĐẶT NGÔN NGỮ',
    select_language: 'Chọn Ngôn Ngữ',
    
    // Ad System
    watch_ad: 'XEM QUẢNG CÁO',
    free_rewards: 'Phần Thưởng Miễn Phí',
    daily_rewards: 'Phần Thưởng Hàng Ngày',
    streak: 'Chuỗi',
    claim: 'NHẬN!',
    cooldown: 'Thời Gian Chờ',
    revive_earth: 'Hồi Sinh Trái Đất',
    double_credits: 'Nhân Đôi Tín Dụng',
    tower_boost: 'Tăng Cường Tháp',
    pet_boost: 'Tăng Cường Pet',
    
    // Game Over
    game_over: 'KẾT THÚC GAME',
    final_level: 'Cấp Cuối',
    monsters_killed: 'Quái Vật Tiêu Diệt',
    bosses_killed: 'Boss Tiêu Diệt',
    rewards: 'Phần Thưởng',
    total_gold: 'Tổng Vàng',
    total_ruby: 'Tổng Ruby',
    bonus: 'Thưởng',
    restart: 'CHƠI LẠI',
    main_menu: 'MENU CHÍNH',
    
    // Notifications
    earth_unlocked: 'Mở Khóa Trái Đất Mới!',
    tower_unlocked: 'Mở Khóa Tháp Mới!',
    pet_unlocked: 'Mở Khóa Pet Mới!',
    level_up: 'LÊN CẤP!',
    boss_warning: 'BOSS ĐANG ĐẾN!',
    purchase_successful: 'Mua Thành Công!',
    upgrade_successful: 'Nâng Cấp Thành Công!',
    
    // Common UI
    ok: 'OK',
    cancel: 'HỦY',
    yes: 'CÓ',
    no: 'KHÔNG',
    loading: 'Đang tải...',
    awesome: 'TUYỆT VỜI!',
    congratulations: 'Chúc mừng!',
    error: 'Lỗi',
    warning: 'Cảnh báo',
    info: 'Thông tin',
    close: 'ĐÓNG',
    continue: 'TIẾP TỤC',
  },
  
  zh: {
    // Main Menu
    game_title: '地球防御',
    game_subtitle: '塔防游戏',
    start: '开始游戏',
    profile: '资料',
    achievements: '成就',
    leaderboard: '排行榜',
    settings: '设置',
    back: '返回',
    version: '版本 1.0.0',
    
    // Game UI
    level: '等级',
    xp: '经验',
    kills: '击杀',
    credits: '积分',
    gold: '金币',
    ruby: '红宝石',
    hp: '生命值',
    dr: '防御',
    towers: '塔',
    ability: '技能',
    damage: '伤害',
    range: '射程',
    attack_speed: '攻击速度',
    
    // Earth Selection
    select_earth: '选择地球',
    earth_stats: '地球属性',
    choose: '选择',
    unlock_at_level: '解锁等级',
    max_towers: '最大塔数',
    
    // Earth Types
    earth_classic: '经典地球',
    earth_ice: '冰霜地球',
    earth_volcanic: '火山地球',
    earth_crystal: '水晶地球',
    earth_quantum: '量子地球',
    earth_neutron: '中子地球',
    
    // Earth Descriptions
    earth_classic_desc: '属性均衡，适合新手',
    earth_ice_desc: '生命值高，冰冻附近怪物',
    earth_volcanic_desc: '反弹伤害，支持更多塔',
    earth_crystal_desc: '高抗性，持续产生能量',
    earth_quantum_desc: '每关可传送塔一次',
    earth_neutron_desc: '巨大生命值，减慢所有怪物',
    
    // Tower Names
    laser_tower: '激光塔',
    rapid_fire_tower: '速射塔',
    heavy_cannon: '重炮',
    missile_tower: '导弹塔',
    multi_directional_tower: '多方向塔',
    piercing_tower: '穿透塔',
    circular_tower: '环形塔',
    chain_lightning_tower: '闪电链塔',
    freeze_tower: '冰冻塔',
    shotgun_tower: '霰弹塔',
    beam_tower: '光束塔',
    
    // Tower Descriptions
    laser_tower_desc: '伤害和速度平衡',
    rapid_fire_tower_desc: '攻击速度快，伤害低',
    heavy_cannon_desc: '伤害高，攻击慢',
    missile_tower_desc: '范围爆炸伤害',
    multi_directional_tower_desc: '向4个方向射击',
    piercing_tower_desc: '子弹穿透敌人',
    circular_tower_desc: '全方向射击',
    chain_lightning_tower_desc: '闪电链击3个目标',
    freeze_tower_desc: '减速和冰冻敌人',
    shotgun_tower_desc: '锥形发射5颗子弹',
    beam_tower_desc: '连续光束伤害',
    
    // Pet Names
    laser_pet: '激光宠物',
    missile_pet: '导弹宠物',
    drone_pet: '无人机宠物',
    shield_pet: '护盾宠物',
    support_pet: '支援宠物',
    
    // Pet Descriptions
    laser_pet_desc: '快速激光攻击',
    missile_pet_desc: '爆炸导弹攻击',
    drone_pet_desc: '自主战斗无人机',
    shield_pet_desc: '提供护盾保护',
    support_pet_desc: '增强附近塔',
    
    // Profile UI
    owned: '拥有',
    equipped: '已装备',
    buy: '购买',
    equip: '装备',
    unequip: '卸下',
    upgrade: '升级',
    combine: '合成',
    cost: '价格',
    level_req: '等级要求',
    already_owned: '已拥有',
    not_enough_currency: '货币不足',
    
    // Equipment
    equipment: '装备',
    weapon: '武器',
    armor: '护甲',
    accessory: '配饰',
    special: '特殊',
    common: '普通',
    rare: '稀有',
    epic: '史诗',
    legendary: '传说',
    
    // Achievements
    achievement_unlocked: '成就解锁！',
    achievements_completed: '已完成',
    achievements_progress: '进度',
    claim_reward: '领取！',
    achievement_reward: '成就奖励已领取！',
    
    // Achievement Names
    first_steps: '第一步',
    survivor_10: '幸存者',
    veteran_25: '老兵',
    master_50: '防御大师',
    legendary_100: '传奇守护者',
    exterminator_100: '灭虫者',
    slayer_1000: '怪物杀手',
    boss_hunter_10: 'Boss猎人',
    architect_50: '塔建筑师',
    wealthy_10k: '富有者',
    gem_collector_100: '宝石收集者',
    earth_collector: '星球收集者',
    tower_master: '塔大师',
    perfect_game: '完美防御',
    speed_runner: '速跑者',
    daily_devotee: '每日忠实者',
    
    // Achievement Descriptions
    first_steps_desc: '完成你的第一场游戏',
    survivor_10_desc: '达到10级',
    veteran_25_desc: '达到25级',
    master_50_desc: '达到50级',
    legendary_100_desc: '达到100级',
    exterminator_100_desc: '击杀100个怪物',
    slayer_1000_desc: '击杀1000个怪物',
    boss_hunter_10_desc: '击败10个Boss',
    architect_50_desc: '建造50座塔',
    wealthy_10k_desc: '累计获得10000金币',
    gem_collector_100_desc: '累计获得100红宝石',
    earth_collector_desc: '解锁所有地球类型',
    tower_master_desc: '解锁所有塔类型',
    perfect_game_desc: '完成游戏时地球未受伤害',
    speed_runner_desc: '10分钟内达到20级',
    daily_devotee_desc: '连续登录7天',
    
    // Leaderboard
    highest_level: '最高等级',
    monster_slayer: '怪物杀手',
    survival_time: '生存大师',
    speed_run: '速跑者',
    composite_score: '防御大师',
    rank: '排名',
    player: '玩家',
    score: '分数',
    date: '日期',
    your_best: '你的最佳',
    no_entries: '暂无记录。成为第一个！',
    new_record: '新纪录！',
    leaderboard_stats: '排行榜统计',
    total_entries: '总条目',
    top_score: '最高分',
    average: '平均',
    
    // Seasonal
    season: '赛季',
    time_left: '剩余',
    season_champion: '赛季冠军',
    season_runner_up: '赛季亚军',
    season_bronze: '赛季季军',
    
    // Audio Settings
    audio_settings: '音频设置',
    master_volume: '主音量',
    music_volume: '音乐音量',
    sfx_volume: '音效音量',
    mute: '静音',
    unmute: '取消静音',
    
    // Visual Settings
    visual_settings: '视觉设置',
    show_fps: '显示FPS',
    particle_effects: '粒子效果',
    screen_shake: '屏幕震动',
    quality_presets: '质量预设',
    low: '低',
    medium: '中',
    high: '高',
    
    // Language Settings
    language_settings: '语言设置',
    select_language: '选择语言',
    
    // Ad System
    watch_ad: '观看广告',
    free_rewards: '免费奖励',
    daily_rewards: '每日奖励',
    streak: '连击',
    claim: '领取！',
    cooldown: '冷却时间',
    revive_earth: '复活地球',
    double_credits: '双倍积分',
    tower_boost: '塔增强',
    pet_boost: '宠物增强',
    
    // Game Over
    game_over: '游戏结束',
    final_level: '最终等级',
    monsters_killed: '击杀怪物',
    bosses_killed: '击杀Boss',
    rewards: '奖励',
    total_gold: '总金币',
    total_ruby: '总红宝石',
    bonus: '奖励',
    restart: '重新开始',
    main_menu: '主菜单',
    
    // Notifications
    earth_unlocked: '新地球解锁！',
    tower_unlocked: '新塔解锁！',
    pet_unlocked: '新宠物解锁！',
    level_up: '升级！',
    boss_warning: 'Boss来袭！',
    purchase_successful: '购买成功！',
    upgrade_successful: '升级成功！',
    
    // Common UI
    ok: '确定',
    cancel: '取消',
    yes: '是',
    no: '否',
    loading: '加载中...',
    awesome: '太棒了！',
    congratulations: '恭喜！',
    error: '错误',
    warning: '警告',
    info: '信息',
    close: '关闭',
    continue: '继续',
  },
};

let currentLang: Lang = 'en';

// Load language from localStorage
export function loadLanguagePreference(): void {
  const saved = localStorage.getItem('gameLanguage');
  if (saved && (saved === 'en' || saved === 'vi' || saved === 'zh')) {
    currentLang = saved as Lang;
  } else {
    // Try to detect browser language
    const browserLang = navigator.language || navigator.languages[0] || 'en';
    if (browserLang.startsWith('vi')) {
      currentLang = 'vi';
    } else if (browserLang.startsWith('zh')) {
      currentLang = 'zh';
    } else {
      currentLang = 'en'; // Default to English
    }
    saveLanguagePreference();
  }
}

// Save language to localStorage
export function saveLanguagePreference(): void {
  localStorage.setItem('gameLanguage', currentLang);
}

export function setLang(lang: Lang): void {
  currentLang = lang;
  saveLanguagePreference();
}

export function getLang(): Lang {
  return currentLang;
}

export function getLanguageInfo(lang: Lang): LanguageInfo {
  return SUPPORTED_LANGUAGES.find(l => l.code === lang) || SUPPORTED_LANGUAGES[0] as LanguageInfo;
}

export function getCurrentLanguageInfo(): LanguageInfo {
  return getLanguageInfo(currentLang);
}

export function t(key: string): string {
  const translation = LANG_DATA[currentLang][key];
  if (translation) {
    return translation;
  }
  
  // Fallback to English if key not found in current language
  if (currentLang !== 'en') {
    const englishTranslation = LANG_DATA['en'][key];
    if (englishTranslation) {
      console.warn(`Translation missing for key "${key}" in language "${currentLang}", using English fallback`);
      return englishTranslation;
    }
  }
  
  console.warn(`Translation missing for key "${key}" in all languages`);
  return key;
}

// Initialize language preference on module load
loadLanguagePreference(); 